import Box from "@mui/joy/Box";
import Table from "@mui/joy/Table";
import Tooltip from "@mui/joy/Tooltip";
import dayjs from "dayjs";
import { UnixTimestamp } from "../../../../models/common";
import { unixToDayjs } from "../../../utils/time";
import { ClinicalEvent } from "../../../../models/clinical_event";

interface FactsYearCalendarProps {
  readonly tx_facts: { [key: string]: Array<ClinicalEvent> };
  readonly mostRecentDate: UnixTimestamp;
  readonly mostAncientDate: UnixTimestamp;
  readonly tooltipDateTitle: any;
  readonly treatmentLengthDisplay: any;
  readonly openDrawer: (name: string, facts: ClinicalEvent[]) => void;
}

export default function FactsYearCalendar({
  tx_facts,
  mostRecentDate,
  mostAncientDate,
  tooltipDateTitle,
  treatmentLengthDisplay,
  openDrawer,
}: FactsYearCalendarProps) {
  // for the yearly view
  let yearCount =
    unixToDayjs(mostRecentDate)!
      .startOf("years")
      .diff(unixToDayjs(mostAncientDate)?.startOf("years"), "years") + 1;

  // Helper function to render year headers
  const renderYearHeaders = () => {
    return Array.from({ length: yearCount }, (_, i) => i)
      .reverse()
      .map((i) => {
        const year = dayjs(mostRecentDate).subtract(i, "year");
        return (
          <th
            key={year.format("YYYY")}
            colSpan={12}
            style={{
              backgroundColor: "background.surface",
              textAlign: "center",
              borderBottom: "1px solid var(--TableCell-borderColor)",
              borderRight: "2px solid var(--TableCell-borderColor)",
            }}
          >
            {year.format("YYYY")}
          </th>
        );
      });
  };

  // Helper function to render treatment box for a specific fact
  const renderTreatmentBox = (
    fact: ClinicalEvent,
    medication: string,
    month: dayjs.Dayjs,
    year: dayjs.Dayjs
  ) => {
    const treatmentStart = dayjs(fact.start_date);
    const treatmentEnd = fact.end_date ? dayjs(fact.end_date) : treatmentStart.add(1, "day");
    const treatmentLengthInDays = treatmentEnd.diff(treatmentStart, "days");
    const dayInMonth = treatmentStart.date();

    if (!treatmentStart.isSame(month, "month") || !treatmentStart.isSame(year, "year")) {
      return null;
    }

    return (
      <Tooltip title={tooltipDateTitle(fact, 49)} key={fact.start_date} arrow placement="top">
        <Box
          sx={{
            position: "absolute",
            width:
              treatmentLengthInDays > 1
                ? "calc(" + (treatmentLengthInDays * 1.6 - dayInMonth) + "px)"
                : "8px",
            background: fact.probability ? "#A1E8A1" : "#F3C896",
            height: "calc(100% - 16px)",
            borderRadius: "16px",
            marginLeft:
              dayInMonth > 15 ? dayInMonth * 1.3 - 24 + "px" : -24 + dayInMonth * 1.3 + "px",
            fontWeight: "bold",
            color: fact.probability ? "#0A470A" : "#7A3E0A",
            zIndex: 1,
            cursor: "pointer",
          }}
          onClick={() => {
            openDrawer(medication, [fact]);
          }}
        >
          {treatmentLengthDisplay(fact.start_date, fact.end_date, 49)}
        </Box>
      </Tooltip>
    );
  };

  // Helper function to render month cells for a given year
  const renderMonthCells = (year: dayjs.Dayjs, facts: ClinicalEvent[], medication: string) => {
    return Array.from({ length: 12 }, (_, monthIndex) => {
      const month = year.set("month", monthIndex);
      return (
        <td
          key={month.format("YYYY-MM")}
          style={{
            position: "relative",
            textAlign: "center",
            borderBottom: "1px solid var(--TableCell-borderColor)",
            borderRight:
              monthIndex === 11
                ? "2px solid var(--TableCell-borderColor)"
                : "1px solid var(--TableCell-borderColor)",
            padding: "8px 24px",
          }}
        >
          {facts.map((fact) => renderTreatmentBox(fact, medication, month, year))}
        </td>
      );
    });
  };

  // Helper function to render year cells for a medication row
  const renderYearCells = (facts: ClinicalEvent[], medication: string) => {
    return Array.from({ length: yearCount }, (_, i) => i)
      .reverse()
      .map((i) => {
        const year = dayjs(mostRecentDate).subtract(i, "year");
        return renderMonthCells(year, facts, medication);
      });
  };

  return (
    <Table
      sx={{
        display: "block",
        overflowX: "auto",
        whiteSpace: "nowrap",
        background: "white",
        "& tr > *:first-of-type": {
          position: "sticky",
          left: 0,
          boxShadow: "1px 0 var(--TableCell-borderColor)",
          bgcolor: "background.surface",
          overflowX: "scroll",
          zIndex: 2,
        },
      }}
      borderAxis="xBetween"
    >
      <thead>
        <tr>
          <th
            style={{
              borderBottom: "1px solid var(--TableCell-borderColor)",
            }}
          ></th>
          {renderYearHeaders()}
        </tr>
      </thead>
      <tbody>
        {Object.entries(tx_facts).map(([medication, facts]) => (
          <tr key={medication}>
            <td style={{ verticalAlign: "middle" }}>{medication}</td>
            {renderYearCells(facts, medication)}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
